import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock, Check } from "lucide-react";
import { BLOG_POSTS, getPost, formatDate } from "../data/blog";
import { Seo } from "../components/Seo";
import { CONTACT } from "../data/content";

const ease = [0.22, 1, 0.36, 1];

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        headline: post.title,
        description: post.metaDescription,
        datePublished: post.date,
        dateModified: post.date,
        inLanguage: "de-DE",
        keywords: post.keywords.join(", "),
        author: { "@type": "Person", name: CONTACT.owner },
        publisher: { "@type": "Organization", name: CONTACT.company },
        articleSection: post.category,
      },
      post.faq?.length && {
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ].filter(Boolean),
  };

  return (
    <main className="pt-[76px]" data-testid="blog-post-page">
      <Seo title={post.metaTitle} description={post.metaDescription} path={`/blog/${post.slug}`} type="article" image={post.image} jsonLd={jsonLd} />

      <article>
        <header className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--gold)] transition-colors" data-testid="blog-post-back">
            <ArrowLeft size={14} /> Alle Beiträge
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 mt-10 items-end">
            <div className="lg:col-span-8">
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="eyebrow mb-6">{post.category}</motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease }}
                className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight"
                data-testid="blog-post-title"
              >
                {post.title}
              </motion.h1>
            </div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="lg:col-span-4 flex lg:flex-col lg:items-end gap-4 lg:gap-2 font-mono text-[0.65rem] tracking-[0.2em] uppercase text-[var(--muted-2)]">
              <span>Von {CONTACT.owner}</span>
              <time dateTime={post.date}>{formatDate(post.date)}</time>
              <span className="flex items-center gap-1.5"><Clock size={11} /> {post.readTime} Min. Lesezeit</span>
            </motion.div>
          </div>
        </header>

        <motion.figure
          initial={{ opacity: 0, clipPath: "inset(8% 4% 8% 4%)" }}
          animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.3, delay: 0.2, ease }}
          className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-12"
        >
          <div className="frame frame-corner aspect-[21/9] border border-[var(--line)]">
            <img src={post.image} alt={post.imageAlt} className="absolute inset-0 w-full h-full object-cover" data-testid="blog-post-image" />
          </div>
        </motion.figure>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3 order-2 lg:order-1">
            <div className="lg:sticky lg:top-28 space-y-8">
              <div>
                <p className="eyebrow !text-[0.6rem] mb-4">Inhalt</p>
                <ol className="space-y-2.5 border-l border-[var(--line)] pl-4">
                  {post.sections.map((s, i) => (
                    <li key={s.h2}><a href={`#abschnitt-${i + 1}`} className="text-sm text-[var(--muted)] hover:text-[var(--gold)] transition-colors leading-snug block">{s.h2}</a></li>
                  ))}
                </ol>
              </div>
              <div className="card-glass p-6">
                <p className="eyebrow !text-[0.6rem] mb-3">Direkt umsetzen</p>
                <p className="text-sm text-[var(--muted)] leading-relaxed">Individuelle KI-Produktbilder ab 7,50 € pro Bild – inklusive Korrekturschleife.</p>
                <Link to="/" state={{ scrollTo: "kontakt" }} className="btn-gold mt-5 !py-3 !px-5 text-xs" data-testid="blog-post-cta">Angebot anfragen <ArrowUpRight size={14} /></Link>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8 lg:col-start-5 order-1 lg:order-2" data-testid="blog-post-content">
            <p className="font-display italic text-xl lg:text-2xl text-[var(--gold-3)] leading-snug">{post.excerpt}</p>
            {post.sections.map((s, i) => (
              <section key={s.h2} id={`abschnitt-${i + 1}`} className="mt-12 scroll-mt-28">
                <h2 className="font-display text-2xl lg:text-3xl leading-tight text-[var(--text)]">{s.h2}</h2>
                {s.p?.map((para, k) => <p key={k} className="mt-5 text-[var(--muted)] leading-[1.85] text-[0.98rem]">{para}</p>)}
                {s.list && (
                  <ul className="mt-5 space-y-3">
                    {s.list.map((li) => (
                      <li key={li} className="flex items-start gap-3 text-[var(--muted)] leading-relaxed"><Check size={15} className="text-[var(--gold)] mt-1.5 shrink-0" />{li}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {post.faq?.length > 0 && (
              <section className="mt-16 border-t border-[var(--line)] pt-10" data-testid="blog-post-faq">
                <p className="eyebrow mb-6">Häufige Fragen zum Thema</p>
                <dl className="space-y-6">
                  {post.faq.map((f) => (
                    <div key={f.q}>
                      <dt className="font-display text-xl text-[var(--text)]">{f.q}</dt>
                      <dd className="mt-2 text-[var(--muted)] leading-relaxed">{f.a}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            <div className="mt-16 card-glass p-8 flex items-start gap-5" data-testid="blog-post-author">
              <img src="/images/logo-mark.png" alt="" className="h-10 w-auto mt-1" />
              <div>
                <p className="eyebrow !text-[0.6rem]">Über den Autor</p>
                <p className="font-display text-xl mt-1">{CONTACT.owner}, Gründer von {CONTACT.company}</p>
                <p className="mt-3 text-sm text-[var(--muted)] leading-relaxed">Spezialisiert auf KI-gestützte Produktvisualisierungen für Amazon, eBay, Onlineshops und Social Media – individuell erstellt und für den jeweiligen Einsatzzweck optimiert. Bad Oeynhausen, Deutschland.</p>
              </div>
            </div>
          </div>
        </div>
      </article>

      <section className="border-t border-[var(--line)] bg-[var(--bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <p className="eyebrow mb-8">Weitere Beiträge</p>
          <div className="grid md:grid-cols-3 gap-5" data-testid="blog-post-related">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group border border-[var(--line)] hover:border-[var(--line-strong)] transition-colors bg-[var(--bg)]" data-testid={`blog-related-${p.slug}`}>
                <div className="frame aspect-[3/2]"><img src={p.image} alt={p.imageAlt} className="w-full h-full object-cover" loading="lazy" /><div className="veil" /></div>
                <div className="p-6">
                  <p className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[var(--gold)]">{p.category}</p>
                  <h3 className="font-display text-xl leading-snug mt-3 group-hover:text-[var(--gold-3)] transition-colors">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
